// src/lib/svg/booleanOps.js Bezier-preserving boolean operations on closed paths. Uses subdivision for curve-curve intersections and graph traversal for boolean operations. Supports multi-contour subjects (shapes with holes).
//
// Data format: curve8 = [ax, ay, cp1x, cp1y, cp2x, cp2y, bx, by] A closed contour is curve8[] where last curve's endpoint ≈ first curve's startpoint. A multi-contour shape is curve8[][] — array of contours (outer + holes). Fill rule: even-odd. A point is "inside" if an odd number of contours contain it.

// ─── Bezier primitives ──────────────────────────────────────────────

/** Evaluate cubic bezier at parameter t. */
export function evaluateBezier(c, t) {
	const u = 1 - t
	const uu = u * u,
		tt = t * t
	const uuu = uu * u,
		ttt = tt * t
	return [
		uuu * c[0] + 3 * uu * t * c[2] + 3 * u * tt * c[4] + ttt * c[6],
		uuu * c[1] + 3 * uu * t * c[3] + 3 * u * tt * c[5] + ttt * c[7],
	]
}

/** Evaluate cubic bezier tangent (derivative) at parameter t. */
export function bezierTangent(c, t) {
	const u = 1 - t
	return [
		3 * u * u * (c[2] - c[0]) + 6 * u * t * (c[4] - c[2]) + 3 * t * t * (c[6] - c[4]),
		3 * u * u * (c[3] - c[1]) + 6 * u * t * (c[5] - c[3]) + 3 * t * t * (c[7] - c[5]),
	]
}

/** De Casteljau split at t. Returns { left, right } curve8 arrays. */
export function splitBezierAt(c, t) {
	const [ax, ay, c1x, c1y, c2x, c2y, bx, by] = c
	const u = 1 - t
	const x01 = u * ax + t * c1x,
		y01 = u * ay + t * c1y
	const x12 = u * c1x + t * c2x,
		y12 = u * c1y + t * c2y
	const x23 = u * c2x + t * bx,
		y23 = u * c2y + t * by
	const x012 = u * x01 + t * x12,
		y012 = u * y01 + t * y12
	const x123 = u * x12 + t * x23,
		y123 = u * y12 + t * y23
	const mx = u * x012 + t * x123,
		my = u * y012 + t * y123
	return {
		left: [ax, ay, x01, y01, x012, y012, mx, my],
		right: [mx, my, x123, y123, x23, y23, bx, by],
	}
}

/** Split a curve at multiple sorted t values. Returns array of curve8. */
export function splitBezierAtMultiple(curve, tValues) {
	if (!tValues.length) return [curve]
	const sorted = [...tValues].sort((a, b) => a - b)
	const pieces = []
	let remaining = curve
	let prevT = 0
	for (const t of sorted) {
		const localT = (t - prevT) / (1 - prevT)
		if (localT <= 1e-9 || localT >= 1 - 1e-9) continue
		const { left, right } = splitBezierAt(remaining, localT)
		pieces.push(left)
		remaining = right
		prevT = t
	}
	pieces.push(remaining)
	return pieces
}

/** Extract sub-curve for parameter range [t0, t1]. */
function subcurve(c, t0, t1) {
	if (t0 <= 1e-12 && t1 >= 1 - 1e-12) return c
	let sub = c
	if (t0 > 1e-12) {
		sub = splitBezierAt(sub, t0).right
		t1 = (t1 - t0) / (1 - t0)
	}
	if (t1 < 1 - 1e-12) {
		sub = splitBezierAt(sub, t1).left
	}
	return sub
}

/** Axis-aligned bounding box of a curve8. Returns { minX, minY, maxX, maxY }. */
function curveBounds(c) {
	const minX = Math.min(c[0], c[2], c[4], c[6])
	const maxX = Math.max(c[0], c[2], c[4], c[6])
	const minY = Math.min(c[1], c[3], c[5], c[7])
	const maxY = Math.max(c[1], c[3], c[5], c[7])
	return { minX, minY, maxX, maxY }
}

/** Check if two bounding boxes overlap (with tolerance). */
function boundsOverlap(a, b, tol = 1e-6) {
	return a.maxX + tol >= b.minX && b.maxX + tol >= a.minX && a.maxY + tol >= b.minY && b.maxY + tol >= a.minY
}

/** Bounding box of a path (curve8[]). */
export function pathBounds(curves) {
	let minX = Infinity,
		minY = Infinity,
		maxX = -Infinity,
		maxY = -Infinity
	for (const c of curves) {
		const b = curveBounds(c)
		if (b.minX < minX) minX = b.minX
		if (b.minY < minY) minY = b.minY
		if (b.maxX > maxX) maxX = b.maxX
		if (b.maxY > maxY) maxY = b.maxY
	}
	return { minX, minY, maxX, maxY }
}

/** Check if a curve8 is effectively a straight line (control points collinear with endpoints). */
function isLinear(c) {
	const dx = c[6] - c[0],
		dy = c[7] - c[1]
	const len = Math.sqrt(dx * dx + dy * dy)
	if (len < 1e-10) return true
	// Distance of control points from line
	const nx = -dy / len,
		ny = dx / len
	const d1 = Math.abs(nx * (c[2] - c[0]) + ny * (c[3] - c[1]))
	const d2 = Math.abs(nx * (c[4] - c[0]) + ny * (c[5] - c[1]))
	return d1 < 0.01 && d2 < 0.01
}

/** Line-line intersection. Returns { t1, t2, point } or null. */
function lineLineIntersection(c1, c2) {
	const x1 = c1[0],
		y1 = c1[1],
		x2 = c1[6],
		y2 = c1[7]
	const x3 = c2[0],
		y3 = c2[1],
		x4 = c2[6],
		y4 = c2[7]
	const denom = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
	if (Math.abs(denom) < 1e-12) return null // parallel
	const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / denom
	const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / denom
	if (t < -1e-8 || t > 1 + 1e-8 || u < -1e-8 || u > 1 + 1e-8) return null
	return {
		t1: Math.max(0, Math.min(1, t)),
		t2: Math.max(0, Math.min(1, u)),
		point: [x1 + t * (x2 - x1), y1 + t * (y2 - y1)],
	}
}

export function bezierBezierIntersections(c1, c2) {
	// Fast path for line-line intersection
	if (isLinear(c1) && isLinear(c2)) {
		const r = lineLineIntersection(c1, c2)
		return r ? [r] : []
	}

	const results = []
	subdivisionIntersect(c1, c2, results, 0, 1, 0, 1, 0)
	// Deduplicate close results
	const deduped = []
	for (const r of results) {
		let isDup = false
		for (const d of deduped) {
			if (Math.abs(d.t1 - r.t1) < 1e-4 && Math.abs(d.t2 - r.t2) < 1e-4) {
				isDup = true
				break
			}
		}
		if (!isDup) deduped.push(r)
	}
	return deduped
}

/**
 * Recursive subdivision approach for curve-curve intersection. Simpler and more robust than fat-line clipping.
 */
function subdivisionIntersect(c1, c2, results, tMin1, tMax1, tMin2, tMax2, depth) {
	if (depth > 50 || results.length > 20) return

	const s1 = subcurve(c1, tMin1, tMax1)
	const s2 = subcurve(c2, tMin2, tMax2)
	const b1 = curveBounds(s1)
	const b2 = curveBounds(s2)

	if (!boundsOverlap(b1, b2, 0.1)) return

	// Convergence: if both subcurves are small enough, report intersection
	const size1 = Math.max(b1.maxX - b1.minX, b1.maxY - b1.minY)
	const size2 = Math.max(b2.maxX - b2.minX, b2.maxY - b2.minY)

	if (size1 < 0.5 && size2 < 0.5) {
		const t1 = (tMin1 + tMax1) / 2
		const t2 = (tMin2 + tMax2) / 2
		results.push({ t1, t2, point: evaluateBezier(c1, t1) })
		return
	}

	// Subdivide the larger curve
	if (size1 >= size2) {
		const tMid = (tMin1 + tMax1) / 2
		subdivisionIntersect(c1, c2, results, tMin1, tMid, tMin2, tMax2, depth + 1)
		subdivisionIntersect(c1, c2, results, tMid, tMax1, tMin2, tMax2, depth + 1)
	} else {
		const tMid = (tMin2 + tMax2) / 2
		subdivisionIntersect(c1, c2, results, tMin1, tMax1, tMin2, tMid, depth + 1)
		subdivisionIntersect(c1, c2, results, tMin1, tMax1, tMid, tMax2, depth + 1)
	}
}

// ─── Winding number for point-in-bezier-path ────────────────────────

/**
 * Compute winding number of point (px, py) with respect to a closed bezier path. Uses ray casting along +X direction, counting signed crossings.
 */
function windingNumber(px, py, path) {
	let winding = 0
	for (const c of path) {
		winding += curveCrossings(px, py, c)
	}
	return winding
}

/**
 * Count signed crossings of a horizontal ray from (px, py) going right with a cubic bezier curve. Uses subdivision for robustness.
 */
function curveCrossings(px, py, c) {
	// Quick Y-range reject
	const minY = Math.min(c[1], c[3], c[5], c[7])
	const maxY = Math.max(c[1], c[3], c[5], c[7])
	if (py < minY || py > maxY) return 0

	// Quick X reject — if point is to the right of all control points
	const maxX = Math.max(c[0], c[2], c[4], c[6])
	if (px > maxX) return 0

	// Subdivide and count crossings on sub-curves
	return subdivideForCrossings(px, py, c, 0)
}

function subdivideForCrossings(px, py, c, depth) {
	if (depth > 20) {
		// Approximate as line segment
		return lineCrossing(px, py, c[0], c[1], c[6], c[7])
	}

	// Check if curve's Y range is small enough to treat as monotone
	const ySpan = Math.max(c[1], c[3], c[5], c[7]) - Math.min(c[1], c[3], c[5], c[7])
	if (ySpan < 0.5 || depth > 12) {
		return lineCrossing(px, py, c[0], c[1], c[6], c[7])
	}

	// Split at midpoint
	const { left, right } = splitBezierAt(c, 0.5)
	return subdivideForCrossings(px, py, left, depth + 1) + subdivideForCrossings(px, py, right, depth + 1)
}

function lineCrossing(px, py, x0, y0, x1, y1) {
	if ((y0 <= py && y1 <= py) || (y0 > py && y1 > py)) return 0
	// X of crossing
	const t = (py - y0) / (y1 - y0)
	const cx = x0 + t * (x1 - x0)
	if (cx <= px) return 0
	// Upward crossing = +1, downward = -1
	return y1 > y0 ? 1 : -1
}

/**
 * Test if point is inside a closed bezier path (nonzero winding rule).
 */
export function pointInBezierPath(px, py, path) {
	return windingNumber(px, py, path) !== 0
}

/**
 * Test if point is inside a multi-contour shape using even-odd fill rule. Counts total ray crossings across all contours.
 */
export function pointInShapeEvenOdd(px, py, contours) {
	let crossings = 0
	for (const path of contours) {
		crossings += Math.abs(windingNumber(px, py, path))
	}
	return crossings % 2 === 1
}

// ─── Boolean operations (ported from Paper.js) ─────────────────────
// The boolean pipeline is now in paperBooleans.js — re-export the public API.

export {
	booleanSubtract,
	booleanSubtractEvenodd,
	booleanUnite,
	booleanIntersect,
	booleanExclude,
	resolveContours,
} from './paperBooleans'
